import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

const buttonVariants = cva(
    "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-white transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-950 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
    {
        variants: {
            variant: {
                default:
                    "hover:bg-secondary text-white bg-primary ease-in-out transition-colors duration-300",
                destructive: "bg-[#d50000] hover:bg-[#ff2d00] text-neutral-50",
                outline:
                    "border border-neutral-200 bg-white hover:bg-neutral-100 hover:text-neutral-900",
                disconnect:
                    "border border-neutral-200 bg-white hover:bg-neutral-100 text-red-600 hover:text-secondary",
                secondary:
                    "bg-primary text-white hover:bg-secondary transition-colors duration-300",
                outv2: "border border-neutral-200 bg-white hover:bg-secondary hover:text-white",
                ghost: "hover:bg-neutral-100 hover:text-neutral-900",
                link: "text-neutral-900 underline-offset-4 hover:underline",
                green: "bg-[#45BF55] text-white hover:bg-[#45BF55] ease-in-out transition-colors duration-300",
                outv3: "bg-[#fff] text-[#4B5563] border border-[#4B5563] ease-in-out transition-colors duration-300 hover:bg-secondary hover:text-white",
            },
            size: {
                default: "h-10 px-4 py-2",
                sm: "h-9 rounded-md px-3",
                lg: "h-11 rounded-md px-8",
                icon: "h-10 w-10",
            },
        },
        defaultVariants: {
            variant: "default",
            size: "default",
        },
    }
);

export interface ButtonProps
    extends React.ButtonHTMLAttributes<HTMLButtonElement>,
        VariantProps<typeof buttonVariants> {
    asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
    ({ className, variant, size, asChild = false, ...props }, ref) => {
        const Comp = asChild ? Slot : "button";
        return (
            <Comp
                className={buttonVariants({ variant, size, className })}
                ref={ref}
                {...props}
            />
        );
    }
);
Button.displayName = "Button";

export { Button, buttonVariants };
